import * as React from 'react';

import { findIndex } from 'lodash';
import { GetProductsByIdBatchedQuery } from 'packages/shared/definitions/types';

import { useBreakpointValue } from '@notino/react-styleguide';

import { IProduct } from '@containers/ProductListing/model';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import {
  ProductEventWither,
  useTrackDynamicList,
} from '@helpers/googleTagManager';

import { BULK_CALL_TIMEOUT } from '../../ProductsSection/ProductSectionContent/constants';

interface UseTrackExtrasArgs {
  listName: string;
  trackingEventName: string;
  trackingEventType: string;
  trackingEventAction: string;
  catalogIds: string[];
  data: GetProductsByIdBatchedQuery['vpProductByIds'];
  wasScrollLeft: boolean;
}

interface TrackElementClickArgs {
  timing: number;
  name: string;
  type: string;
  action: string;
  interaction?: string;
}

export const trackElementClick = ({
  action,
  name,
  timing,
  type,
  interaction = 'click',
}: TrackElementClickArgs) => {
  dispatchTrackingEvent({
    event: 'element_click',
    _clear: true,
    element: {
      timing,
      interaction,
      name,
      type,
      action,
    },
  });
};

export const useTrackExtras = ({
  data,
  listName,
  trackingEventAction,
  trackingEventName,
  trackingEventType,
  wasScrollLeft,
  catalogIds,
}: UseTrackExtrasArgs) => {
  const isDesktop = useBreakpointValue({ md: true });
  const { getTimeFromInit } = useTrackingContext();

  const trackVisibleItems = useTrackDynamicList({
    delay: BULK_CALL_TIMEOUT,
    listName,
  });

  const onNewItemShow = React.useCallback(
    (id: IProduct['id']) => {
      if (data) {
        const index = findIndex(data, { id });

        if (index !== -1) {
          const product = data[index];
          if (product) {
            trackVisibleItems(
              ProductEventWither()
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .withVpProduct(product as any)
                .withDiscountPrices(product.attributes)
                .withAdditionalData({
                  id: product.id ?? '',
                  list_position:
                    catalogIds.findIndex(
                      (productId) => productId === String(product.catalogId)
                    ) + 1,
                })
                .build() as Parameters<typeof trackVisibleItems>[0]
            );
          }
        }
      }

      if (isDesktop || wasScrollLeft) {
        return;
      }

      trackElementClick({
        timing: getTimeFromInit(),
        interaction: 'swipe',
        name: trackingEventName,
        type: trackingEventType,
        action: trackingEventAction,
      });
    },
    [
      catalogIds,
      data,
      getTimeFromInit,
      isDesktop,
      trackVisibleItems,
      trackingEventAction,
      trackingEventName,
      trackingEventType,
      wasScrollLeft,
    ]
  );

  const onSlideChange = () => {
    trackElementClick({
      timing: getTimeFromInit(),
      interaction: 'swipe',
      name: trackingEventName,
      type: trackingEventType,
      action: trackingEventAction,
    });
  };

  return { onSlideChange, onNewItemShow };
};
